import { FC, createContext, useContext, ReactNode, useRef } from 'react'

import { TimelineCoord } from 'models'

const START_YEAR = 1953
const YEARS_COUNT = 70
const INTERSECTION_PROBABILITY = 0.25 // 0 = 0%, 0.25 = 25%, 1 = 100%

const TIMELINE_OFFSET = 220
const TIMELINE_SPACING = 60

type DigitsContextType = {
  timelineCoords: TimelineCoord[]
  width: number
  TIMELINE_OFFSET: typeof TIMELINE_OFFSET
  TIMELINE_SPACING: typeof TIMELINE_SPACING
  mapCoordsToYear: (coords: number) => string
  mapYearToCoords: (year: string) => number | ''
}

const DigitsContext = createContext<DigitsContextType | null>(null)

const generateTimeline = () => {
  const lineOptions = [
    [1, 2, 3],
    [0, 2, 3],
    [0, 1, 3],
    [0, 1, 2],
    [0, 1, 2, 3],
  ]
  let previousLine = 4

  const coords = [...Array(YEARS_COUNT)].map((_, i) => {
    const line =
      lineOptions[previousLine][
        Math.round(Math.random() * (lineOptions[previousLine].length - 1))
      ]
    previousLine = line

    return {
      value: START_YEAR + i,
      line: line as TimelineCoord['line'],
      isIntersecting: Math.random() < INTERSECTION_PROBABILITY,
      x: TIMELINE_OFFSET + i * TIMELINE_SPACING,
    }
  })

  const breakpoints = coords.reduce(
    (acc, coord, index) => ({
      ...acc,
      [TIMELINE_OFFSET - 170 + index * TIMELINE_SPACING - 18]:
        coord.value.toString(),
      [TIMELINE_OFFSET - 170 + index * TIMELINE_SPACING + 18]: '',
    }),
    { 0: '' }
  )

  return { coords, breakpoints }
}

export const DigitsContextProvider: FC<{
  children?: ReactNode | ReactNode[]
}> = ({ children }) => {
  const timeline = useRef(generateTimeline())

  const mapCoordsToYear = (coords: number) => {
    let output = ''

    Object.keys(timeline.current.breakpoints).forEach(c => {
      const cNum = Number(c)
      if (cNum <= coords) {
        output =
          timeline.current.breakpoints[
            cNum as keyof typeof timeline.current.breakpoints
          ]
      } else {
        return
      }
    })

    return output
  }

  const mapYearToCoords = (year: string) => {
    const coords = Object.keys(timeline.current.breakpoints)
    const startCoordIndex = coords.findIndex(
      c =>
        timeline.current.breakpoints[
          Number(c) as keyof typeof timeline.current.breakpoints
        ] === year
    )

    if (startCoordIndex < 0) {
      return ''
    }

    const startCoord = Number(coords[startCoordIndex])

    if (!coords[startCoordIndex + 1]) {
      return startCoord
    }

    const nextCoord = Number(coords[startCoordIndex + 1])

    return Math.round((startCoord + nextCoord) / 2)
  }

  return (
    <DigitsContext.Provider
      value={{
        timelineCoords: timeline.current.coords,
        width:
          TIMELINE_OFFSET +
          TIMELINE_SPACING * (timeline.current.coords.length + 1),
        TIMELINE_OFFSET,
        TIMELINE_SPACING,
        mapCoordsToYear,
        mapYearToCoords,
      }}
    >
      {children}
    </DigitsContext.Provider>
  )
}

export const useDigitsContext = () => {
  const context = useContext(DigitsContext)
  return context as DigitsContextType
}
